import type { RenewalDetails, RenewalNote } from '@policyvision/contracts';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/common/components/ui/button';
import { Textarea } from '@/common/components/ui/textarea';
import { useAddRenewalNotes } from '@/renewal/hooks/useAddRenewalNote';

import { RenewalNotesTabContent } from './RenewalNotesTabContent';

interface IProps {
  renewalDetails: RenewalDetails | null;
  isLoading: boolean;
}

const RenewalNotesTab = ({ renewalDetails, isLoading }: IProps) => {
  const { t } = useTranslation();
  const { addNote } = useAddRenewalNotes();
  const [noteText, setNoteText] = useState('');

  const groupedNotes = useMemo(() => {
    return (renewalDetails?.notes ?? [])
      .sort((noteA, noteB) => {
        return noteA.createdAt.getTime() - noteB.createdAt.getTime();
      })
      .reduce(
        (groups, note) => {
          const dateKey = note.createdAt.toLocaleDateString('fr-CA', {
            year: 'numeric',
            month: 'long',
          });
          if (!groups[dateKey]) {
            groups[dateKey] = [];
          }
          groups[dateKey].push(note);
          return groups;
        },
        {} as Record<string, RenewalNote[]>,
      );
  }, [renewalDetails]);

  const handleAddNote = (content: string) => {
    if (!renewalDetails) return;
    addNote(
      {
        renewalUuid: renewalDetails.uuid,
        content: content,
      },
      {
        onSuccess: () => {
          setNoteText('');
        },
        onError: (error: unknown) => {
          const errorData = error as {
            response?: { data?: { message?: string } };
          };
          const errorMessage =
            errorData?.response?.data?.message ||
            t('renewal.drawer.addNoteError');
          toast.error(errorMessage);
        },
      },
    );
  };
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-4 py-4 mb-[150px]">
        <RenewalNotesTabContent
          isLoading={isLoading}
          hasNotes={!!renewalDetails?.notes.length}
          groupedNotes={groupedNotes}
        />
      </div>

      <div className="border-t border-sidebar-border px-4 py-4 bg-primary-foreground absolute bottom-0 w-full">
        <div className="flex gap-3 mb-3">
          <Textarea
            placeholder={t('renewal.notesTab.addNotePlaceholder')}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            className="flex-1 min-h-[60px] resize-none"
          />
        </div>
        <div className="flex">
          <Button
            disabled={!noteText}
            size="sm"
            onClick={() => handleAddNote(noteText)}
            className="w-full bg-primary hover:bg-terra-cotta-600 text-white"
          >
            {t('actions.confirm')}
          </Button>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-3">
          ⚠&nbsp;&nbsp;{t('renewal.notesTab.syncWarning')}
        </p>
      </div>
    </div>
  );
};

export default RenewalNotesTab;
